import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navigation from '../components/Navigation';
import { getAuthHeaders } from '../lib/auth';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HRPayroll() {
  const [employees, setEmployees] = useState([]);
  const [name, setName] = useState('');
  const [salary, setSalary] = useState('');
  const [loading, setLoading] = useState(false);

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/hr/employees`, {
        headers: getAuthHeaders()
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);
      toast.error('Failed to load employees');
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const addEmployee = async () => {
    if (!name || !salary) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/hr/employees`,
        null,
        {
          params: { name, salary: Number(salary) },
          headers: getAuthHeaders()
        }
      );
      setEmployees([...employees, response.data]);
      setName('');
      setSalary('');
      toast.success('Employee added successfully');
    } catch (error) {
      console.error('Failed to add employee:', error);
      toast.error('Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">HR / Payroll</h1>
            <p className="text-gray-600">Manage your team and payroll</p>
          </div>
        </div>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Employee Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input 
                placeholder="Employee Name" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
              />
              <Input 
                type="number" 
                placeholder="Annual Salary" 
                value={salary} 
                onChange={(e) => setSalary(e.target.value)} 
              />
              <Button onClick={addEmployee} disabled={loading}>
                {loading ? 'Adding...' : 'Add Employee'}
              </Button>
            </div>
            <div className="border rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left">
                    <th className="p-3">#</th>
                    <th className="p-3">Name</th>
                    <th className="p-3">Salary</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="p-6 text-center text-gray-500">
                        No employees yet. Add your first employee above.
                      </td>
                    </tr>
                  ) : (
                    employees.map((e, i) => (
                      <tr key={e.id} className="border-t hover:bg-gray-50">
                        <td className="p-3">{i + 1}</td>
                        <td className="p-3">{e.name}</td>
                        <td className="p-3 font-medium">
                          ${e.salary?.toLocaleString?.() || 0}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
